import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _regeneratorRuntime from "@babel/runtime/regenerator";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import React from 'react';
import globalConfig from 'config';
import withRedux from 'next-redux-wrapper';
import dynamic from 'next/dynamic';
import { CookiesProvider } from 'react-cookie';
import { Provider, connect } from 'react-redux';
import appModule, { getCookies, getRandomNumberGenerator, setPageWillUnmount } from './appModule';
import AppProvider from './AppProvider';
import bentoApiModule, { getSession, loadSession, setSessionToken, VisitorStatus } from './bentoApiModule';
import configureStore, { getDefaultExtensions } from './configureStore';
import domainModule from './domainModule';
import logger from './logger';
import RenderTime from './RenderTime';
import timeModule, { isTimeTravelActive, setPageRenderDate } from './timeModule';
import useDomain from './useDomain';
var debug = logger.extend('reduxExtension');
var BentoApiAdminPanel = dynamic(function () {
  return import(
  /* webpackChunkName: "AdminTools" */
  './BentoApiAdminPanel');
});
var TimeTravelAdminPanel = dynamic(function () {
  return import(
  /* webpackChunkName: "AdminTools" */
  './TimeTravelAdminPanel');
}); // It will be very rare to show this alert, and overrides are disabled in
// production anyway. So lazy load it, and render only when the `domain.source`
// is `cookie`.

var DomainOverrideAlert = dynamic(function () {
  return import(
  /* webpackChunkName: "DomainOverrideAlert" */
  './DomainOverrideAlert');
}, {
  ssr: false
});

function ConditionalDomainAlert() {
  var domain = useDomain();
  return domain.source === 'cookie' ? /*#__PURE__*/React.createElement(DomainOverrideAlert, null) : null;
}

export default function reduxExtension() {
  var makeStore = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : configureStore;
  var config = arguments.length > 1 ? arguments[1] : undefined;
  var options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  return function (registry) {
    return {
      id: 'redux',
      redux: function redux(context) {
        return {
          modules: [appModule, domainModule, bentoApiModule, timeModule],
          extensions: getDefaultExtensions(context)
        };
      },
      admin: {
        panels: function panels(initialProps) {
          var har = initialProps.har,
              harFile = initialProps.harFile;
          return [/*#__PURE__*/React.createElement(BentoApiAdminPanel, {
            key: "bentoApi",
            har: har,
            harFile: harFile
          }), /*#__PURE__*/React.createElement(TimeTravelAdminPanel, {
            key: "timeTravel"
          })];
        }
      },
      server: process.browser ? {} : {
        init: function init() {
          require('./config');
        },
        configure: function configure(server) {
          // Import these here so they don't impact the browser bundle size.
          var _require = require('./anonymousServerSessionMiddleware'),
              anonymousServerSessionMiddleware = _require["default"];

          var _require2 = require('./universalCookieExpress'),
              universalCookieMiddleware = _require2["default"];

          var _require3 = require('./bentoApiExpressMiddleware'),
              bentoApiExpressMiddleware = _require3["default"];

          var _require4 = require('./automatedTestCookieMiddleware'),
              automatedTestCookieMiddleware = _require4["default"]; // Abundance of caution: ensure backwards compatibility if someone
          // upgrades `@techstyle/redux-core` without upgrading
          // `@techstyle/next-server`. TODO: This can be removed when everyone
          // is up to date.


          var addMiddleware = function addMiddleware(name, middleware) {
            if (server.useTracked) {
              server.useTracked(name, middleware);
            } else {
              server.use(middleware);
            }
          };

          var getIsAnonymousServerSessionEnabled = options.getIsAnonymousServerSessionEnabled;
          addMiddleware('anonymousServerSessionMiddleware', anonymousServerSessionMiddleware(getIsAnonymousServerSessionEnabled));
          addMiddleware('universalCookieMiddleware', universalCookieMiddleware());
          addMiddleware('bentoApiExpressMiddleware', bentoApiExpressMiddleware(options));
          addMiddleware('automatedTestCookieMiddleware', automatedTestCookieMiddleware());
        }
      },
      app: {
        enhance: function enhance(App) {
          return withRedux(makeStore, _objectSpread({
            registry: registry
          }, config))(App);
        },
        getInitialProps: {
          enhance: function enhance(getInitialProps) {
            return /*#__PURE__*/function () {
              var _ref2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(_ref) {
                var Component, ctx, useUnsafeAuth, _ctx$req, _ctx$req$context, _process, _process$env, _getSession, isAdmin, sessionToken, initialProps, setCookies, timeTravelActive, _getSession2, _isAdmin, _ctx$req$context2, har, shouldSendHarLogs, correlationId, harFile, cookies, serverOnlyCookies;

                return _regeneratorRuntime.wrap(function _callee$(_context) {
                  while (1) {
                    switch (_context.prev = _context.next) {
                      case 0:
                        Component = _ref.Component, ctx = _ref.ctx;

                        if (process.browser) {
                          _context.next = 12;
                          break;
                        }

                        useUnsafeAuth = globalConfig.get('server.bentoApi.unsafeAuth');

                        if (!useUnsafeAuth) {
                          _context.next = 11;
                          break;
                        }

                        _context.next = 6;
                        return ctx.store.dispatch(loadSession());

                      case 6:
                        _getSession = getSession(ctx.store.getState()), isAdmin = _getSession.isAdmin;
                        sessionToken = ctx === null || ctx === void 0 ? void 0 : (_ctx$req = ctx.req) === null || _ctx$req === void 0 ? void 0 : (_ctx$req$context = _ctx$req.context) === null || _ctx$req$context === void 0 ? void 0 : _ctx$req$context.readSessionToken();

                        if (!(sessionToken && (((_process = process) === null || _process === void 0 ? void 0 : (_process$env = _process.env) === null || _process$env === void 0 ? void 0 : _process$env.NODE_CONFIG_ENV) !== 'production' || isAdmin))) {
                          _context.next = 11;
                          break;
                        }

                        _context.next = 11;
                        return ctx.store.dispatch(setSessionToken(sessionToken));

                      case 11:
                        if (!ctx.req.context.anonymousServerSession) {
                          debug('anonymousServerSession is false; caching will be disabled.'); // TODO: As long as we set `private`, maybe we can increase the
                          // `max-age` to that this user's browser caches the response
                          // for them only?

                          ctx.res.set('Cache-Control', 'private, max-age=0');
                        }

                      case 12:
                        ctx.store.dispatch(setPageWillUnmount(false));
                        ctx.random = ctx.store.dispatch(getRandomNumberGenerator());
                        ctx.store.dispatch(setPageRenderDate());
                        _context.prev = 15;
                        _context.next = 18;
                        return getInitialProps({
                          Component: Component,
                          ctx: ctx
                        });

                      case 18:
                        initialProps = _context.sent;

                      case 19:
                        _context.prev = 19;

                        if (!process.browser) {
                          setCookies = ctx.req.context.setCookies; // If have cookies and have not sent response headers
                          // then append additional cookies
                          // ie: redirecting(302) an already logged in user

                          if (setCookies.size && !ctx.res.headersSent) {
                            debug('Appending %s received Set-Cookie %s to response.', setCookies.size, setCookies.size === 1 ? 'header' : 'headers');
                            setCookies.forEach(function (cookie) {
                              var name = cookie.name,
                                  value = cookie.value,
                                  options = _objectWithoutProperties(cookie, ["name", "value"]);

                              ctx.req.universalCookies.set(name, value, options);
                            });
                          } // If we're in a time travel session, or the user is an admin,
                          // then we want to disable caching.


                          if (ctx.req.context.anonymousServerSession) {
                            timeTravelActive = isTimeTravelActive(ctx.store.getState());
                            _getSession2 = getSession(ctx.store.getState()), _isAdmin = _getSession2.isAdmin;

                            if (timeTravelActive || _isAdmin) {
                              ctx.res.set('Cache-Control', 'private, max-age=0');
                            }
                          }
                        }

                        return _context.finish(19);

                      case 22:
                        if (process.browser) {
                          _context.next = 25;
                          break;
                        }

                        _context.next = 25;
                        return ctx.store.endRootSaga();

                      case 25:
                        if (!process.browser) {
                          _ctx$req$context2 = ctx.req.context, har = _ctx$req$context2.har, shouldSendHarLogs = _ctx$req$context2.shouldSendHarLogs, correlationId = _ctx$req$context2.correlationId;

                          if (har && shouldSendHarLogs(ctx)) {
                            // Generate a reasonable filename for the HAR file.
                            harFile = "ssr_".concat(ctx.pathname // Add `index` onto paths that end with `/`.
                            .replace(/\/+$/, '/index') // Remove leading slash.
                            .replace(/^\/+/, '') // Replace all remaining slashes with `_`.
                            .replace(/\/+/g, '_'), "_").concat(correlationId, ".har");
                            initialProps = _objectSpread(_objectSpread({}, initialProps), {}, {
                              har: har,
                              harFile: harFile
                            });
                          }
                        }

                        if (!process.browser) {
                          cookies = ctx.store.dispatch(getCookies()); // We don't want to transfer cookies in the HTML payload from the
                          // server because (1) the browser already has them so it's a waste
                          // of bytes, and (2) some of these cookies might be marked
                          // HttpOnly, so we'd be violating their policy by transmitting
                          // them in a method readable by JavaScript on the page. So, we
                          // wrap the cookies in an object with `toJSON()` defined to return
                          // nothing.

                          serverOnlyCookies = {
                            cookies: cookies,
                            toJSON: function toJSON() {
                              return undefined;
                            }
                          };
                          initialProps = _objectSpread(_objectSpread({}, initialProps), {}, {
                            serverOnlyCookies: serverOnlyCookies
                          });
                        }

                        return _context.abrupt("return", initialProps);

                      case 28:
                      case "end":
                        return _context.stop();
                    }
                  }
                }, _callee, null, [[15,, 19, 22]]);
              }));

              return function (_x) {
                return _ref2.apply(this, arguments);
              };
            }();
          }
        },
        render: function render(initialProps, children) {
          var store = initialProps.store,
              serverOnlyCookies = initialProps.serverOnlyCookies;
          var cookies = serverOnlyCookies ? serverOnlyCookies.cookies : // Retrieve post configure store creation Cookies instance from context
          store.dispatch(getCookies());
          return /*#__PURE__*/React.createElement(CookiesProvider, {
            cookies: cookies
          }, /*#__PURE__*/React.createElement(Provider, {
            store: store
          }, /*#__PURE__*/React.createElement(AppProvider, null, /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(ConditionalDomainAlert, null), /*#__PURE__*/React.createElement(RenderTime, null, children)))));
        }
      },
      page: function page(options) {
        var mapStateToProps = options.mapStateToProps,
            mapDispatchToProps = options.mapDispatchToProps,
            _options$sessionRequi = options.sessionRequired,
            sessionRequired = _options$sessionRequi === void 0 ? true : _options$sessionRequi,
            loggedInRedirectUrl = options.loggedInRedirectUrl;
        return {
          getInitialProps: function getInitialProps(ctx) {
            return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2() {
              var visitorStatus;
              return _regeneratorRuntime.wrap(function _callee2$(_context2) {
                while (1) {
                  switch (_context2.prev = _context2.next) {
                    case 0:
                      if (!(sessionRequired || loggedInRedirectUrl)) {
                        _context2.next = 3;
                        break;
                      }

                      _context2.next = 3;
                      return ctx.store.dispatch(loadSession());

                    case 3:
                      if (!loggedInRedirectUrl) {
                        _context2.next = 10;
                        break;
                      }

                      visitorStatus = ctx.store.getState().session.visitorStatus;

                      if (!(visitorStatus === VisitorStatus.LOGGED_IN)) {
                        _context2.next = 10;
                        break;
                      }

                      if (!ctx.res) {
                        _context2.next = 9;
                        break;
                      }

                      // redirect already logged in user on the server
                      ctx.res.redirect(302, loggedInRedirectUrl);
                      return _context2.abrupt("return");

                    case 9:
                      window.location.href = loggedInRedirectUrl;

                    case 10:
                    case "end":
                      return _context2.stop();
                  }
                }
              }, _callee2);
            }))();
          },
          // Instead of needing to use both `connect()` and `createPage()`, wrap
          // pages in `connect()` automatically if the options contain
          // `mapStateToProps` or `mapDispatchToProps`.
          enhance: mapStateToProps || mapDispatchToProps ? connect(mapStateToProps, mapDispatchToProps) : undefined
        };
      }
    };
  };
}